import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
} from '@mm/state/selectors/viewSelectors';
import { Status, ThreeDotMenu } from '@mm/components';
import { colors } from '@mm/utils';
import { activeIntegrationSelector } from '@mm/state/modules/connectionsTableState/selectors';
import {
  FACEBOOK_ADS,
  useConnectionsTableActions,
} from '@mm/state/modules/connectionsTableState/actions';
import { FacebookAdsTable } from './FacebookAdsTable';
import {
  ComponentDataItem,
  renderDataCell,
} from '../../../../../../contacts/components/ContactsTable/ContactTable';
import { GridCell } from '../../../../../../../../common/components/Grid/Grid';
import { EditOutlined } from '@ant-design/icons';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useAdAudienceCRUDActions } from '@mm/state/actions/AdAudienceCRUDActions';
import { useAdAccount } from './use-ad-account';
import { selectActiveAdAccount } from '@mm/state/selectors/adAccountsSelecotrs';
import AdAudienceModel from '@mm/state/model/AdAudienceModel';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import { Link } from 'react-router-dom';
import store from '@mm/state/store';

type IntegrationEntity = AdAudienceModel;
type CellProps = { item: IntegrationEntity };

const renderStatusCell = ({ item }: CellProps) => {
  const { synchronizeAutomatically } = item;
  return (
    <GridCell>
      <Status
        status={synchronizeAutomatically ? 'Automatically' : 'Manually'}
        color={synchronizeAutomatically ? colors.green : colors.grayLight}
      />
    </GridCell>
  );
};

const renderAudienceCell = ({ item }: CellProps) => {
  const { audience } = item;
  const chatbotId = activeChatbotIdSelector(store.getState());
  return (
    <GridCell>
      {audience ? (
        <Link
          key={audience?.id}
          to={BotBuilderRoutes.audiences.getLink({
            chatbotId,
            audienceId: audience?.id,
          })}
        >
          {audience?.name}
        </Link>
      ) : (
        <span>-</span>
      )}
    </GridCell>
  );
};

const renderActionsCell = (
  item: IntegrationEntity,
  editSheet: (id: number) => void,
  deleteConnection: (item: IntegrationEntity) => void,
) => {
  const onSelectItem = (menuItem: string) => {
    switch (menuItem) {
      case 'delete':
        return deleteConnection(item);
      default:
        break;
    }
  };

  const elements = [{ name: 'Delete', value: 'delete' }];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <GridCell className="action-cell">
      <EditOutlined
        onClick={() => editSheet(item.id)}
        className="integrations__edit-icon"
      />
      <ThreeDotMenu
        className="integrations__three-dot-menu"
        elements={elements}
        selectItem={onSelectItem}
        onClick={onMenuClick}
      />
    </GridCell>
  );
};

const generateTableCells = (
  editSheet: (id: number) => void,
  deleteConnection: (item: IntegrationEntity) => void,
  onChangeOrder: (orderBy: string) => void,
) => {
  const cells: ComponentDataItem[] = [
    {
      label: 'Audiences',
      keyName: 'audience',
      onChange: onChangeOrder,
      Cell: renderAudienceCell,
    },
    {
      label: 'Facebook Audience',
      keyName: 'facebook_audience',
      onChange: onChangeOrder,
      Cell: renderDataCell('name'),
    },
    {
      label: 'Automatically',
      keyName: 'sync_automatically',
      onChange: onChangeOrder,
      Cell: renderStatusCell,
    },
    {
      label: 'Actions',
      keyName: 'actions',
      Cell: ({ item }: CellProps) =>
        renderActionsCell(item, editSheet, deleteConnection),
    },
  ];
  return cells;
};

export const FacebookAdsIntegrations = () => {
  const { setConnectionsOrderBy, setActiveIntegration } =
    useConnectionsTableActions();
  const activeIntegration = useSelector(activeIntegrationSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const adAudienceCRUDActions = useAdAudienceCRUDActions();
  const { showSlideoutMenu } = useViewActions();
  const { openDeleteConfirmModal } = useModalActions();

  const activeAdAccount = useSelector(selectActiveAdAccount);
  const adAccount = useAdAccount();

  const onEdit = (id: number) => {
    setActiveIntegrationHandler();
    showSlideoutMenu('fbAdsIntegrationEditor', {
      adAudienceId: id,
    });
  };
  const onDelete = (item: IntegrationEntity) => {
    const deleteCallback = () => {
      adAudienceCRUDActions.deleteAudienceByRemoteId(
        facebookPageId,
        item.remoteId,
      );
    };

    openDeleteConfirmModal({
      headerText: `Are you sure you want to delete ${item.name}?`,
      deleteCallback,
      newStyle: true,
    });
  };

  const onChangeOrder = orderBy => {
    setConnectionsOrderBy(orderBy);
  };

  const setActiveIntegrationHandler = () => {
    if (activeIntegration === FACEBOOK_ADS) return;
    setActiveIntegration(FACEBOOK_ADS);
  };

  const cells = generateTableCells(onEdit, onDelete, onChangeOrder);

  useEffect(() => {
    if (!activeAdAccount) {
      adAccount.fetchAccounts();
    }
  }, []);

  useEffect(() => {
    if (activeAdAccount?.id) {
      adAudienceCRUDActions.retrieveAll(facebookPageId, activeAdAccount?.id);
    }
  }, [activeAdAccount?.id]);

  return <FacebookAdsTable cellData={cells} name={FACEBOOK_ADS} />;
};
